import { ANALYTICS_API_BASE } from "#app/lib/api-bases";
import { getActiveWorkspaceId } from "#app/lib/data/data-provider";
import { getDeviceContext } from "./device-context";
import { expandLegacyAliases, type LegacyAliasEvent, type TrackEvent } from "@salestrak/analytics-taxonomy";

const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE_SIZE = 1_000;
const MAX_RETRIES = 3;
// keepalive fetch bodies are capped at 64KB by spec — stay well under
const KEEPALIVE_MAX_BYTES = 60_000;

export type { TrackEvent } from "@salestrak/analytics-taxonomy";

type QueuedEvent = (TrackEvent | LegacyAliasEvent) & {
  _ts: number;
  _retries: number;
  // The workspace active when the event happened. Read at track time, not at
  // flush time: a switch between the two would bill the event to the wrong
  // workspace (including `workspace.switch` itself).
  _workspaceId: string | null;
};

// The server stamps created_at at insert time, which is wrong by up to
// minutes for events delivered late (retries, unload beacons) — send the
// client-side occurrence time alongside each event.
//
// Device context rides here rather than at the call sites: it is the same for
// every event in the tab, so 79 `track()` callers would otherwise each have to
// remember to attach it. Both transports funnel through this function, so the
// unload beacon carries it too.
function toPayload(batch: QueuedEvent[]) {
  const device = getDeviceContext();
  return batch.map(({ _ts, _retries, _workspaceId, ...evt }) => ({
    ...evt,
    device,
    occurredAt: new Date(_ts).toISOString(),
  }));
}

type GetAccessToken = () => Promise<string | null>;

export class AnalyticsClient {
  private queue: QueuedEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private getAccessToken: GetAccessToken;
  private flushing = false;
  // The flush on the wire, so flushAll can wait for it instead of reading
  // "busy" as "done".
  private inFlight: Promise<void> | null = null;
  private lastToken: string | null = null;

  constructor(getAccessToken: GetAccessToken) {
    this.getAccessToken = getAccessToken;
  }

  start() {
    if (this.flushTimer) return;
    // Prime the cached token so flushBeacon can authenticate even when the
    // tab closes before the first successful flush (short first sessions).
    void this.getAccessToken()
      .then((token) => {
        if (token) this.lastToken = token;
      })
      .catch(() => {});
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  stop() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }

  track(event: TrackEvent) {
    // Migration-window dual-write: the canonical event plus any legacy-named
    // mirror / legacy payload keys, driven by the taxonomy's alias maps.
    for (const wireEvent of expandLegacyAliases(event)) {
      this.enqueue(wireEvent);
    }
    if (this.queue.length >= BATCH_SIZE) {
      this.flush();
    }
  }

  private enqueue(event: TrackEvent | LegacyAliasEvent) {
    if (this.queue.length >= MAX_QUEUE_SIZE) {
      // Drop oldest events to make room
      this.queue.splice(0, this.queue.length - MAX_QUEUE_SIZE + 1);
    }
    this.queue.push({
      ...event,
      _ts: Date.now(),
      _retries: 0,
      _workspaceId: getActiveWorkspaceId(),
    });
  }

  /**
   * Drain the whole queue while the session is still valid. Sign-out awaits
   * this before it clears the tokens: an event queued after the last timed
   * flush (`sign_out` itself) has no token to send with once they are gone.
   * Stops at the first batch that fails, so a dead network cannot spin here.
   */
  async flushAll() {
    if (this.inFlight) await this.inFlight;
    while (this.queue.length > 0) {
      const before = this.queue.length;
      await this.flush();
      if (this.queue.length >= before) return;
    }
  }

  /**
   * Forget the session: the queue and the cached token. Runs after sign-out,
   * so events that could not be sent are dropped instead of going out later
   * under the next user who signs in to this tab.
   */
  reset() {
    this.queue = [];
    this.lastToken = null;
  }

  flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0) return this.inFlight ?? Promise.resolve();
    this.flushing = true;
    const sending = this.sendBatch().finally(() => {
      this.flushing = false;
      this.inFlight = null;
    });
    this.inFlight = sending;
    return sending;
  }

  private async sendBatch() {
    const batch = this.takeBatch();

    try {
      const token = await this.getAccessToken();
      if (!token) {
        // Not authenticated — put events back
        this.queue.unshift(...batch);
        return;
      }
      this.lastToken = token;

      const response = await fetch(`${ANALYTICS_API_BASE}/v1/events/batch`, {
        method: "POST",
        headers: requestHeaders(token, batch[0]?._workspaceId ?? null),
        body: JSON.stringify({ events: toPayload(batch) }),
      });

      if (!response.ok) {
        this.requeueWithRetryLimit(batch);
      }
    } catch {
      this.requeueWithRetryLimit(batch);
    }
  }

  /**
   * Synchronous fire-and-forget flush for page unload.
   * Uses fetch with keepalive so the request survives after the page closes.
   * Body is chunked to stay under the spec's 64KB keepalive cap.
   * Falls back to navigator.sendBeacon if keepalive throws.
   */
  flushBeacon() {
    if (this.queue.length === 0 || !this.lastToken) return;

    const batch = this.queue.splice(0);
    const url = `${ANALYTICS_API_BASE}/v1/events/batch`;

    for (const [workspaceId, events] of groupByWorkspace(batch)) {
      const headers = requestHeaders(this.lastToken, workspaceId);
      for (const chunk of chunkByByteSize(toPayload(events), KEEPALIVE_MAX_BYTES)) {
        const body = JSON.stringify({ events: chunk });
        try {
          // A failing keepalive fetch shows up as an async rejection, not a
          // synchronous throw — fall back to sendBeacon on both paths.
          fetch(url, { method: "POST", headers, body, keepalive: true }).catch(() =>
            sendBeaconFallback(url, body),
          );
        } catch {
          sendBeaconFallback(url, body);
        }
      }
    }
  }

  /**
   * The next batch: up to BATCH_SIZE events from the head of the queue that
   * share one workspace, because the workspace rides on the request header.
   */
  private takeBatch(): QueuedEvent[] {
    const workspaceId = this.queue[0]?._workspaceId ?? null;
    let size = 0;
    while (
      size < this.queue.length &&
      size < BATCH_SIZE &&
      this.queue[size]!._workspaceId === workspaceId
    ) {
      size++;
    }
    return this.queue.splice(0, size);
  }

  private requeueWithRetryLimit(batch: QueuedEvent[]) {
    const retryable = batch.filter((evt) => evt._retries < MAX_RETRIES);
    if (retryable.length > 0) {
      const incremented = retryable.map((evt) => ({
        ...evt,
        _retries: evt._retries + 1,
      }));
      this.queue.unshift(...incremented);
    }
  }
}

function requestHeaders(token: string, workspaceId: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  if (workspaceId) {
    headers["workspaceid"] = workspaceId;
  }
  return headers;
}

function groupByWorkspace(events: QueuedEvent[]): Map<string | null, QueuedEvent[]> {
  const groups = new Map<string | null, QueuedEvent[]>();
  for (const event of events) {
    const group = groups.get(event._workspaceId);
    if (group) group.push(event);
    else groups.set(event._workspaceId, [event]);
  }
  return groups;
}

// Best-effort: sendBeacon can't carry the Authorization header, but it's the
// only transport left once keepalive fetch has failed during unload.
function sendBeaconFallback(url: string, body: string) {
  try {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
  } catch {
    // Events lost on page close — acceptable for analytics
  }
}

function chunkByByteSize<T>(items: T[], maxBytes: number): T[][] {
  const chunks: T[][] = [];
  let current: T[] = [];
  let currentBytes = 2; // for "[]"
  for (const item of items) {
    const itemBytes = new Blob([JSON.stringify(item)]).size + 1; // + comma
    if (current.length > 0 && currentBytes + itemBytes > maxBytes) {
      chunks.push(current);
      current = [];
      currentBytes = 2;
    }
    current.push(item);
    currentBytes += itemBytes;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}
